import React, {
  FunctionComponent,
  JSXElementConstructor,
  ReactElement
} from 'react'

import {
  RESPONSE_PENDING,
  RESPONSE_OFFLINE,
  RESPONSE_BADRESPONSE,
  RESPONSE_BADDATA,
  RESPONSE_UNAUTHORIZED,
  RESPONSE_NOTFOUND,
  RESPONSE_FORBIDDEN
} from 'libraries/front-end-core/constants/responses'

import Error from './Error'
import Offline from './Offline'
import Unauthorized from './Unauthorized'
import Forbidden from './Forbidden'
import { groupBy, includes, isUndefined } from 'libraries/lodash'
import { SkeletonType } from 'types/Skeleton'
import Loading from './Loading'

// loading skeletons
import LegislationAccordionSkeletonContainer from 'redux/containers/components/accordion/LegislationAccordionSkeletonContainer'
import QaAccordionSkeletonContainer from 'redux/containers/components/accordion/QaAccordionSkeletonContainer'
import DashboardSkeletonContainer from 'redux/containers/pages/dashboard/DashboardSkeletonContainer'
import SearchResultsSkeletonContainer from 'redux/containers/pages/search/SearchResultsSkeletonContainer'
import TiledMenuSkeleton from 'components/tiled-menu/TiledMenuSkeleton'

const skeletonMap: Record<SkeletonType, JSX.Element> = {
  'legislation-accordion': <LegislationAccordionSkeletonContainer />,
  'qa-accordion': <QaAccordionSkeletonContainer />,
  dashboard: <DashboardSkeletonContainer />,
  'search-results': <SearchResultsSkeletonContainer />,
  'tiled-menu': <TiledMenuSkeleton />,
  'aol-tiled-menu': <TiledMenuSkeleton showAolSections />
}

const ResponseConsumer: FunctionComponent<ResponseConsumerProps> = (props) => {
  const {
    pendingComponent = Loading,
    baddataComponent = Error,
    badresponseComponent = Error,
    notfoundComponent = Error,
    offlineComponent = Offline,
    unauthorizedComponent = Unauthorized,
    forbiddenComponent = Forbidden,
    baddataProps = { title: 'Data loading error' },
    badresponseProps = { title: 'Data loading error' },
    notfoundProps = { title: 'Not found' },
    offlineProps = { title: 'Not available offline' },
    unauthorizedProps = { title: 'Not logged in' },
    forbiddenProps = { title: 'Not authorised' },
    content,
    response,
    responses = [],
    skeleton,
    debugStatus
  } = props

  const renderStatuses = (statuses: Response[]): React.ReactElement | null => {
    const grouped = groupBy(statuses)
    if (RESPONSE_PENDING in grouped) {
      if (skeleton !== undefined) {
        return skeletonMap[skeleton]
      } else {
        return <Render component={pendingComponent} />
      }
    }
    if (RESPONSE_BADDATA in grouped) {
      return <Render component={baddataComponent} props={baddataProps} />
    }
    if (RESPONSE_BADRESPONSE in grouped) {
      return (
        <Render component={badresponseComponent} props={badresponseProps} />
      )
    }
    if (RESPONSE_NOTFOUND in grouped) {
      return <Render component={notfoundComponent} props={notfoundProps} />
    }
    if ((RESPONSE_OFFLINE as unknown as string) in grouped) {
      return <Render component={offlineComponent} props={offlineProps} />
    }
    if (RESPONSE_UNAUTHORIZED in grouped) {
      return (
        <Render component={unauthorizedComponent} props={unauthorizedProps} />
      )
    }
    if (RESPONSE_FORBIDDEN in grouped) {
      return <Render component={forbiddenComponent} props={forbiddenProps} />
    }
    return null
  }

  const renderError = (e: string): React.ReactElement => {
    console.error(e)
    return <Render component={Error} />
  }

  if (debugStatus !== undefined) {
    return renderStatuses([debugStatus])
  }

  try {
    const allResponses: Response[] = responses.concat(
      isUndefined(response) ? [] : [response]
    )
    const notOk: any = allResponses.reduce(
      (a: any, status) => (!responseOk(status) ? [...a, status] : a),
      []
    )

    return (notOk.length as number) !== 0 ? renderStatuses(notOk) : (content?.() ?? null)
  } catch (e) {
    return renderError(e)
  }
}

const Render: FunctionComponent<RenderProps> = ({
  component: Component,
  props
}: RenderProps): React.ReactElement | null => (Component !== null && Component !== undefined) ? <Component {...props} /> : null

const responseOk = (status: any): boolean => {
  return !includes(
    [
      RESPONSE_PENDING,
      RESPONSE_OFFLINE,
      RESPONSE_BADRESPONSE,
      RESPONSE_BADDATA,
      RESPONSE_NOTFOUND,
      RESPONSE_UNAUTHORIZED,
      RESPONSE_FORBIDDEN
    ],
    status
  )
}

interface RenderProps {
  component: JSXElementConstructor<any> | null
  props?: any
}

interface TitleProps {
  title: string
}

type Response = string | number | boolean | object | any[]

interface ResponseConsumerProps {
  pendingComponent?: JSXElementConstructor<any>
  baddataProps?: TitleProps
  baddataComponent?: JSXElementConstructor<any> | null
  badresponseProps?: TitleProps
  badresponseComponent?: JSXElementConstructor<any> | null
  notfoundProps?: TitleProps
  notfoundComponent?: JSXElementConstructor<any>
  offlineProps?: TitleProps
  offlineComponent?: JSXElementConstructor<any>
  unauthorizedProps?: TitleProps
  unauthorizedComponent?: JSXElementConstructor<any>
  forbiddenProps?: TitleProps
  forbiddenComponent?: JSXElementConstructor<any>
  response?: Response
  responses?: Response[]
  content?: () => ReactElement<any>
  skeleton?: SkeletonType
  debugStatus?: number
}

export default ResponseConsumer
